const STORAGE_KEY = 'aacore.platformAskAi.v1';

export type PlatformAskAiSessionState = {
  serial: number;
  active: number;
  open: boolean;
  chats: unknown[];
};

function canUseSessionStorage(): boolean {
  try {
    return typeof sessionStorage !== 'undefined';
  } catch {
    return false;
  }
}

export function loadPlatformAskAiSession(): PlatformAskAiSessionState | null {
  if (!canUseSessionStorage()) return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PlatformAskAiSessionState>;
    const serial = Number(parsed?.serial);
    const active = Number(parsed?.active);
    if (!Number.isFinite(serial) || serial < 1) return null;
    if (!Number.isFinite(active) || active < 1) return null;
    if (!Array.isArray(parsed?.chats) || parsed.chats.length === 0) return null;
    return {
      serial,
      active,
      open: Boolean(parsed.open),
      chats: parsed.chats,
    };
  } catch {
    return null;
  }
}

export function savePlatformAskAiSession(state: PlatformAskAiSessionState): void {
  if (!canUseSessionStorage()) return;
  try {
    const chats = (state.chats || []).map((chat) => {
      if (!chat || typeof chat !== 'object') return chat;
      const thread = { ...(chat as { messages?: Array<{ pending?: boolean }>; secrets?: unknown }) };
      delete thread.secrets;
      return {
        ...thread,
        messages: Array.isArray(thread.messages)
          ? thread.messages.filter((message) => !message?.pending)
          : [],
      };
    });
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        serial: state.serial,
        active: state.active,
        open: Boolean(state.open),
        chats,
      })
    );
  } catch {
    // sessionStorage can be unavailable or over quota
  }
}

export function platformAskAiHasInProgressSignup(
  state: PlatformAskAiSessionState | null = loadPlatformAskAiSession()
): boolean {
  const chats = state?.chats || [];
  return chats.some((chat) => {
    if (!chat || typeof chat !== 'object') return false;
    const thread = chat as {
      signup?: { wanted?: boolean; created?: boolean };
      messages?: Array<{ signup?: { wanted?: boolean; created?: boolean } }>;
    };
    if (thread.signup?.wanted && !thread.signup?.created) return true;
    return (thread.messages || []).some((message) => message?.signup?.wanted && !message.signup.created);
  });
}

export function shouldSkipPeriodicRefreshOnPath(pathname: string): boolean {
  const path = String(pathname || '');
  if (path === '/agreements' || path.startsWith('/agreements/')) return true;
  return platformAskAiHasInProgressSignup();
}
